import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { snoozeSubscriptionCancelationBanner } from "./header-service";
import { getUserSubscriptionPlan } from "@/app/(general-header)/subscription/subscription-service";
import { useCompetitionByType } from "@/app/redux/features/categories/selectors";

type CancelledNotificationProps = {
  onSnooze: () => void;
};

const tryAndGetValue = (key: string, metaData: [string, string][]): string => {
  const item = metaData?.find(item => item[0] === key);
  return item ? item[1] : "0";
};

const CancelledNotification = ({ onSnooze }: CancelledNotificationProps) => {
  const [subscriptionPeriodEnd, setSubscriptionPeriodEnd] = useState<number | null>(null);
  const [totalEntries, setTotalEntries] = useState<string | null>(null);
  const [totalPrizeWorth, setTotalPrizeWorth] = useState<string | null>(null);
  const subscriberCategory = useCompetitionByType("subscriberCompetitions");
  const subscriberCompetitionsCount = subscriberCategory?.competitions?.length ?? 0;

  useEffect(() => {
    getUserSubscriptionPlan()
      .then(res => res.json())
      .then((data: SubscriptionPlanUser) => {
        if (data?.current_period_end) {
          setSubscriptionPeriodEnd(data.current_period_end);
        }
        if (data?.metadata) {
          setTotalEntries(tryAndGetValue("total_entries", data.metadata));
          setTotalPrizeWorth(tryAndGetValue("total_prize_worth", data.metadata));
        }
      })
      .catch(err => console.error("Failed to fetch subscription plan for cancellation banner", err));
  }, []);

  const remainingDays = useMemo(() => {
    if (!subscriptionPeriodEnd) return null;
    const diff = dayjs(subscriptionPeriodEnd * 1000).diff(dayjs(), "milliseconds");
    if (diff <= 0) return 0;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }, [subscriptionPeriodEnd]);

  const bannerTitle = useMemo(() => {
    if (remainingDays === null) return null;
    if (remainingDays <= 1) return "Your Pass runs out tonight";
    if (remainingDays === 2) return "Your Pass runs out tomorrow";
    if (remainingDays < 5) return `Your Pass runs out in ${remainingDays} days`;
    return `Your Pass runs out in ${remainingDays} days`;
  }, [remainingDays]);

  const handleSnooze = async () => {
    try {
      const response = await snoozeSubscriptionCancelationBanner();
      const data = await response.json();
      if (data.success) {
        onSnooze();
      }
    } catch (err) {
      console.error("Failed to snooze cancellation banner", err);
    }
  };

  if (bannerTitle === null) return null;
  return (
    <div className={"font-jost bg-white max-lg:pt-4 max-lg:pb-6 max-lg:relative shadow-[inset_0_1px_0_0_#E0E0E0]"}>
      <div className={"lg:relative w-full max-w-[1360px] lg:mx-auto lg:py-12"}>
        <button
          className={"m-0 p-0 border-0 absolute right-3 lg:right-6 top-3 cursor-pointer lg:top-3"}
          onClick={handleSnooze}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className={"size-6"}>
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="#898994"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className={"lg:flex lg:items-end lg:justify-between lg:max-w-[1086px] pl-4 max-lg:pr-4 lg:pl-8"}>
          <div className={"mb-3 lg:mb-0 flex gap-2 items-start lg:max-w-[487px]"}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className={"size-6 lg:size-10 shrink-0"}>
              <path
                d="M2.99219 15.0002H7.99427"
                stroke="#FF8200"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7.99344 18.0002H4.99219"
                stroke="#FF8200"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7.9926 21.0002H6.99219"
                stroke="#FF8200"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M11.7109 7.97656V12.6295L15.3685 14.8594"
                stroke="#FF8200"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M3.05469 10.9955C3.5529 6.49465 7.36548 2.99219 11.9994 2.99219C16.9725 2.99219 21.0032 7.02287 21.0032 11.9959C21.0032 16.969 16.9725 20.9997 11.9994 20.9997"
                stroke="#FF8200"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div>
              <div
                className={"text-[#FF8200] text-sm font-medium leading-5 mb-2 max-lg:pr-4 lg:text-[22px] lg:leading-7"}>
                {bannerTitle}
              </div>
              <div className={"text-[#4c4c52] text-[11px] font-normal leading-[14px] lg:text-base lg:leading-5.5"}>
                {totalEntries && Number(totalEntries) > 0 ? (
                  <>
                    <span className={"font-medium"}>{totalEntries} entries. </span>
                    {totalPrizeWorth ? (
                      <>
                        <span className={"font-medium"}>{totalPrizeWorth}</span> in
                        <span className={"font-medium"}> prizes.</span>
                      </>
                    ) : (
                      ""
                    )}
                    {" Daily exclusive competitions. Once it's gone, so are they."}
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
          <div className={"max-lg:pl-8 lg:max-w-[265px]"}>
            <a
              href="/management-subscription?renew=1&utm_medium=recovery_banner&utm_source=recovery_banner&utm_campaign=recovery_banner"
              className="theme-btn theme-btn-primary theme-btn-xs lg:theme-btn-2lg min-w-[120px] lg:min-w-[265px] uppercase">
              RENEW NOW
              {/*<svg*/}
              {/*  xmlns="http://www.w3.org/2000/svg"*/}
              {/*  width="16"*/}
              {/*  height="16"*/}
              {/*  viewBox="0 0 16 16"*/}
              {/*  fill="none"*/}
              {/*  className={"size-4 lg:size-6 shrink-0"}>*/}
              {/*  <path*/}
              {/*    d="M4.66797 4.66699L8.0013 8.00033L4.66797 11.3337M8.66797 4.66699L12.0013 8.00033L8.66797 11.3337"*/}
              {/*    stroke="white"*/}
              {/*    strokeWidth="1.5"*/}
              {/*    strokeLinecap="round"*/}
              {/*    strokeLinejoin="round"*/}
              {/*  />*/}
              {/*</svg>*/}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelledNotification;
